/**
 * The real numbers behind the investor profile's "social proof" sections.
 *
 * These sections used to render mock data. In demo mode they showed a rating,
 * testimonials, a response rate and DD activity; in production the objects were
 * undefined so the sections silently vanished — except the stats row, which
 * had a HARDCODED `'94%'` fallback and therefore advertised a response rate to
 * founders that nobody had ever measured.
 *
 * Both are now server-derived:
 *   - engagement comes from `get_investor_engagement_metrics`, computed on read
 *     from messages / conversations / dd_reports / data_room_access_requests.
 *   - reviews come from `get_investor_reviews`, which returns only published
 *     rows and masks the author when they asked to be anonymous.
 *
 * EVERY FIELD IS NULLABLE ON PURPOSE. `null` means "not measured", and callers
 * must render nothing rather than a zero — an investor nobody has messaged has
 * no response rate, which is a different statement from a 0% response rate.
 *
 * IDENTITY: both take the AUTH user id, never `investor_profiles.id`. The RPCs
 * bridge to the profile-id space internally, in one place, because
 * conversations and data-room requests key on the profile id while dd_reports
 * and portfolio_companies key on the auth id.
 */

import { useQuery } from '@tanstack/react-query';

import { queryKeys } from '@/lib/supabase/api';
import type { InvestorReview } from '@/lib/investor/reviews';
import { isSupabaseConfigured, supabase } from '@/lib/supabase/client';
import { rpc } from '@/lib/supabase/rpc';
import type { AuthUserId } from '@/lib/ids';

export interface InvestorEngagementMetrics {
  /** Percent of inbound founder threads that got a reply. */
  responseRate: number | null;
  /** Median hours to first reply. Median, so one abandoned thread cannot skew it. */
  medianResponseHours: number | null;
  respondedThreadCount: number | null;
  /** Denominator behind responseRate — lets "100%" off one thread read honestly. */
  eligibleThreadCount: number | null;
  ddInProgress: number | null;
  avgDecisionDays: number | null;
  dataRoomRequests30d: number | null;
}

const EMPTY_METRICS: InvestorEngagementMetrics = {
  responseRate: null,
  medianResponseHours: null,
  respondedThreadCount: null,
  eligibleThreadCount: null,
  ddInProgress: null,
  avgDecisionDays: null,
  dataRoomRequests30d: null,
};

/** Derived engagement metrics for an investor. Takes the AUTH user id. */
export function useInvestorEngagementMetrics(userId: AuthUserId | undefined) {
  return useQuery({
    queryKey: queryKeys.investorEngagement(userId ?? ''),
    enabled: !!userId && isSupabaseConfigured(),
    // Derived on read; a minute of staleness is fine and keeps profile
    // navigation from re-running the aggregation on every focus.
    staleTime: 60_000,
    queryFn: async (): Promise<InvestorEngagementMetrics> => {
      if (!userId) return EMPTY_METRICS;

      const { data, error } = await rpc('get_investor_engagement_metrics', {
        p_investor_user_id: userId,
      });
      if (error) throw error;

      const row = Array.isArray(data) ? data[0] : data;
      if (!row) return EMPTY_METRICS;

      // `?? null` throughout: a missing key and a measured zero must not
      // collapse into the same value.
      return {
        responseRate: row.response_rate ?? null,
        medianResponseHours: row.median_response_hours ?? null,
        respondedThreadCount: row.responded_thread_count ?? null,
        eligibleThreadCount: row.eligible_thread_count ?? null,
        ddInProgress: row.dd_in_progress ?? null,
        avgDecisionDays: row.avg_decision_days ?? null,
        dataRoomRequests30d: row.data_room_requests_30d ?? null,
      };
    },
  });
}

/** Published founder reviews of an investor. Takes the AUTH user id. */
export function useInvestorReviews(userId: AuthUserId | undefined) {
  return useQuery({
    queryKey: queryKeys.investorReviews(userId ?? ''),
    enabled: !!userId && isSupabaseConfigured(),
    queryFn: async (): Promise<InvestorReview[]> => {
      if (!userId) return [];

      const { data, error } = await rpc('get_investor_reviews', {
        p_investor_user_id: userId,
      });
      if (error) throw error;

      return (data ?? []).map((r) => ({
        id: r.id,
        rating: r.rating,
        body: r.body,
        isAnonymous: r.is_anonymous,
        founderName: r.founder_name ?? null,
        companyName: r.company_name ?? null,
        createdAt: r.created_at,
      }));
    },
  });
}

// Re-exported so callers need only one import for "reviews on a profile".
export { averageRating, reviewToTestimonial } from '@/lib/investor/reviews';
export type { InvestorReview } from '@/lib/investor/reviews';
